// ─── Экспорт результатов турнира (текст + CSV) ───────────────────────────────
//
// Внешний рейтинговый сервис принимает результаты ПО ОДНОМУ МАТЧУ (команда A vs
// команда B + счёт), а не турнирную таблицу. Поэтому выгружаем все сыгранные
// (FINISHED) матчи всех туров — для любого формата (Americano / Mexicano /
// Beat the Box, включая оба этапа BtB).
//
// Две формы вывода:
//   • buildReadable — человекочитаемый текст (для ручного ввода по матчу);
//       имя богатое: «Имя (алиас) @ник» — чтобы организатор узнал людей.
//   • buildCsv      — CSV под внешний шаблон импорта; имя ЧИСТОЕ (без @/скобок),
//       т.к. сервис матчит строку с именем аккаунта, а decorations мешают.
//
// Дата матча: event.eventDate, при отсутствии — event.eventCreatedAt (тот же фолбэк,
// что в списке событий). Оба приходят строками с началом «YYYY-MM-DD…».

import { getMemberDisplay } from '../memberDisplayCore';
import { FORMAT_LABELS } from './tournamentConstants';

/** Чистое имя игрока (для CSV): без @ника и скобок. */
export function resolvePlayerName(player) {
  if (!player) return '';
  return getMemberDisplay(player).name || '';
}

/** Богатое имя игрока (для читаемого текста): «Имя (алиас) @ник». */
export function resolvePlayerNameRich(player) {
  if (!player) return '';
  const { name, aliasTag, nick } = getMemberDisplay(player);
  let out = name || '';
  if (aliasTag) out += ` (${aliasTag})`;
  if (nick) out += ` @${nick}`;
  return out;
}

/** 'YYYY-MM-DD' даты матчей: eventDate → eventCreatedAt. Пусто, если нет ни того, ни другого. */
export function resolveMatchDate(tournament) {
  const raw = tournament?.eventDate || tournament?.eventCreatedAt;
  if (!raw) return '';
  // eventDate — наивный LocalDateTime «2026-07-04T17:30:00»,
  // eventCreatedAt — Instant «2026-07-04T09:15:30.123Z». Оба начинаются с YYYY-MM-DD.
  return String(raw).slice(0, 10);
}

/** 'YYYY-MM-DD' → 'DD.MM.YYYY' для читаемой шапки. Пустую строку возвращает как есть. */
function toDisplayDate(ymd) {
  if (!ymd) return '';
  const [y, m, d] = ymd.split('-');
  return d && m && y ? `${d}.${m}.${y}` : ymd;
}

/** Формат матча по размеру команды. */
function formatLabel(tournament) {
  return (tournament?.teamSize || 2) === 1 ? 'Singles' : 'Doubles';
}

/** id → игрок турнира (для резолва имён из teamXPlayerIds). */
function buildPlayerIndex(tournament) {
  const map = new Map();
  (tournament?.players || []).forEach(p => map.set(p.id, p));
  return map;
}

/** Имена команды в порядке ids. resolver — resolvePlayerName / resolvePlayerNameRich. */
function teamNames(ids, index, resolver) {
  return (ids || []).map(id => resolver(index.get(id))).filter(Boolean);
}

/**
 * Плоский список сыгранных матчей в порядке раундов.
 * Каждый элемент: { roundNumber, courtNumber, teamA:[names], teamB:[names], scoreA, scoreB }.
 * resolver задаёт способ отображения имени.
 */
function collectFinishedMatches(tournament, resolver) {
  const index = buildPlayerIndex(tournament);
  const rows = [];
  (tournament?.rounds || []).forEach(round => {
    (round.matches || [])
      .filter(m => m.status === 'FINISHED')
      .forEach(m => {
        rows.push({
          roundNumber: round.roundNumber,
          courtNumber: m.courtNumber,
          teamA: teamNames(m.teamAPlayerIds, index, resolver),
          teamB: teamNames(m.teamBPlayerIds, index, resolver),
          scoreA: m.teamAScore,
          scoreB: m.teamBScore,
        });
      });
  });
  return rows;
}

/** Есть ли хотя бы один сыгранный матч (для показа кнопки). */
export function hasExportableMatches(tournament) {
  return (tournament?.rounds || []).some(r =>
    (r.matches || []).some(m => m.status === 'FINISHED')
  );
}

/** Человекочитаемый текст результатов (группировка по раундам). */
export function buildReadable(tournament) {
  const rows = collectFinishedMatches(tournament, resolvePlayerNameRich);
  const date = toDisplayDate(resolveMatchDate(tournament));
  // Шапка: «<Название события> · <Формат> — <дата>». Название добавляем только если
  // оно задано (иначе показываем один формат, без дублей вроде «Турнир: Турнир»).
  const formatType = FORMAT_LABELS[tournament?.format] || tournament?.format || 'Турнир';
  const heading = [tournament?.eventTitle, formatType].filter(Boolean).join(' · ');
  const lines = [`${heading}${date ? ` — ${date}` : ''}`, ''];

  let currentRound = null;
  rows.forEach(r => {
    if (r.roundNumber !== currentRound) {
      currentRound = r.roundNumber;
      if (lines[lines.length - 1] !== '') lines.push('');
      lines.push(`Раунд ${r.roundNumber}`);
    }
    const a = r.teamA.join(' / ');
    const b = r.teamB.join(' / ');
    const court = r.courtNumber != null ? `Корт ${r.courtNumber}: ` : '';
    lines.push(`  ${court}${a}  —  ${b}    ${r.scoreA}:${r.scoreB}`);
  });

  if (rows.length === 0) lines.push('Нет сыгранных матчей');
  return lines.join('\n');
}

/** Экранирование значения для CSV (запятая-разделитель). */
function csvCell(v) {
  const s = v == null ? '' : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

const CSV_HEADER = [
  'Match Date', 'Event', 'Format',
  'Team1 Player1', 'Team1 Player2', 'Team2 Player1', 'Team2 Player2',
  'Team1 Score', 'Team2 Score',
];

/**
 * CSV под внешний шаблон импорта матчей. Одна строка на матч, чистые имена.
 * Порядок/заголовки колонок предположительны — сверить с реальным шаблоном
 * сервиса при первом импорте (см. план экспорта результатов в docs/).
 */
export function buildCsv(tournament) {
  const rows = collectFinishedMatches(tournament, resolvePlayerName);
  const date = resolveMatchDate(tournament);
  const event = tournament?.eventTitle || '';
  const format = formatLabel(tournament);

  const lines = [CSV_HEADER.map(csvCell).join(',')];
  rows.forEach(r => {
    lines.push([
      date, event, format,
      r.teamA[0] || '', r.teamA[1] || '',
      r.teamB[0] || '', r.teamB[1] || '',
      r.scoreA, r.scoreB,
    ].map(csvCell).join(','));
  });
  return lines.join('\n');
}
